import React from 'react';

import { cva } from 'class-variance-authority';
import { cn } from './utils';

import type { VariantProps } from 'class-variance-authority';

const iconButtonVariants = cva(['disabled:cursor-default inline-flex items-center justify-center'], {
  variants: {
    variant: {
      solid: 'bg-button-secondary hover:bg-button-secondaryHover disabled:bg-button-disabled',
      outline: 'bg-button-outline hover:bg-button-outlineHover ring-[1px] ring-border',
      ghost: 'bg-transparent hover:bg-button-secondaryHover',
    },
    size: {
      medium: 'p-2 rounded-lg',
      small: 'p-1.5 rounded-lg',
    },
  },
  defaultVariants: {
    variant: 'solid',
    size: 'medium',
  },
});

export type IconButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof iconButtonVariants> & {
    state?: 'default' | 'loading';
    keepPointerEvents?: boolean;
  };

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ variant, size, state, disabled, className, keepPointerEvents = false, children, onClick, ...props }, ref) => {
    const isDisabled = state === 'loading' || disabled;

    return (
      <button
        ref={ref}
        type="button"
        disabled={isDisabled}
        className={cn(
          iconButtonVariants({ variant, size }),
          'select-none w-fit h-fit overflow-hidden',
          isDisabled && !keepPointerEvents && 'pointer-events-none',
          className,
        )}
        onClick={
          onClick
            ? e => {
                if (isDisabled) return;
                onClick(e);
              }
            : undefined
        }
        {...props}
      >
        <span className="w-4 h-4 flex items-center justify-center">{children}</span>
      </button>
    );
  },
);

IconButton.displayName = 'IconButton';

export { iconButtonVariants };
